import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { getReceipts, createReceipt, getLocations, getVariants, getSkus, getReceiptTemplates, saveReceiptTemplate, deleteReceiptTemplate, list3plMerchants } from '../lib/api';
import { useCompany, hasFeature } from '../context/CompanyContext';
import { FileArrowDown, Plus, Play, X, FloppyDisk, Trash, Stack } from '@phosphor-icons/react';
import HelpAnchor from '../components/HelpAnchor';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import FilterBar from '../components/FilterBar';
import ReceivingDetail from '../components/ReceivingDetail';
import SkuVariantPicker from '../components/SkuVariantPicker';
import PalletQuickLogDialog from '../components/pallets/PalletQuickLogDialog';
import { matchesCreatedWindow, matchesEtaWindow, CREATED_WINDOW_OPTIONS, ETA_WINDOW_OPTIONS, getEtaChip } from '../lib/dateWindow';
import useUrlFilterState, { useUrlFilterClear } from '../lib/useUrlFilterState';

const EMPTY_LINE = { variant_id: '', expected_qty: '' };

export default function Receiving() {
  const { company } = useCompany();
  const hasThreePlBilling = hasFeature(company, '3pl_billing');
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeReceipt, setActiveReceipt] = useState(null);
  const [locations, setLocations] = useState([]);
  const [variants, setVariants] = useState([]);
  const [skus, setSkus] = useState([]);

  const [newReceipt, setNewReceipt] = useState({
    supplier_name: '', po_number: '', notes: '', expected_arrival: '', expected_items: [], merchant_id: ''
  });
  const [merchants, setMerchants] = useState([]);  // populated when 3pl_billing feature is on
  const [palletLogReceipt, setPalletLogReceipt] = useState(null);  // open: receipt obj; closed: null
  const [expectedLines, setExpectedLines] = useState([{ ...EMPTY_LINE }]);
  const [templates, setTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // ---- Filter bar state (search + supplier + status + ETA/date windows) ----
  // Persisted in the URL so ops can bookmark / share filtered views.
  const [searchTerm, setSearchTerm] = useUrlFilterState('q', '');
  const [supplierFilter, setSupplierFilter] = useUrlFilterState('supplier', '');
  const [statusFilter, setStatusFilter] = useUrlFilterState('status', '');
  const [etaWindow, setEtaWindow] = useUrlFilterState('eta', '');
  const [dateWindow, setDateWindow] = useUrlFilterState('date', '');
  const clearUrlFilters = useUrlFilterClear(['q', 'supplier', 'status', 'eta', 'date']);

  const fetchData = async () => {
    try {
      const [receiptsRes, locsRes, varsRes, skusRes, tplRes] = await Promise.all([
        getReceipts(),
        getLocations({ is_active: true }),
        getVariants(),
        getSkus(),
        getReceiptTemplates(),
      ]);
      setReceipts(receiptsRes.data);
      setLocations(locsRes.data);
      setVariants(varsRes.data);
      setSkus(skusRes.data);
      setTemplates(tplRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Fetch merchants in a SEPARATE effect that watches `hasThreePlBilling`
  // because the company context populates async after first render. Without
  // this, the initial fetch fires while `company` is still null and the
  // dropdown never appears.
  useEffect(() => {
    if (!hasThreePlBilling) {
      setMerchants([]);
      return;
    }
    list3plMerchants({ status: 'active' })
      .then((res) => setMerchants(res.data || []))
      .catch(() => {
        // 402 here just means feature flag flipped off — silently skip.
      });
  }, [hasThreePlBilling]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Build expected_items from the line editor — silently drop blank rows,
      // dedupe variant_ids by summing qty so receivers see one line per SKU.
      const acc = new Map();
      for (const ln of expectedLines) {
        if (!ln.variant_id) continue;
        const qty = parseInt(ln.expected_qty, 10);
        if (!Number.isFinite(qty) || qty <= 0) continue;
        acc.set(ln.variant_id, (acc.get(ln.variant_id) || 0) + qty);
      }
      const expected = Array.from(acc.entries()).map(([variant_id, expected_qty]) => {
        const v = variants.find((x) => x.id === variant_id);
        const sku = v && skus.find((s) => s.id === v.sku_id);
        return {
          variant_id,
          expected_qty,
          sku_id: v?.sku_id || null,
          sku_code: sku?.sku_code || null,
          description: sku?.name || null,
          barcode: v?.barcode || null,
          size: v?.size || null,
          color: v?.color || null,
        };
      });

      const payload = { ...newReceipt, expected_items: expected };
      // Only send expected_arrival when user actually picked a date to keep the
      // payload shape stable for existing consumers.
      if (!payload.expected_arrival) delete payload.expected_arrival;
      // Drop empty merchant_id — undefined merchant means "fall through to
      // default merchant during 3PL accrual" (handled server-side).
      if (!payload.merchant_id) delete payload.merchant_id;
      await createReceipt(payload);
      setShowCreate(false);
      setNewReceipt({ supplier_name: '', po_number: '', notes: '', expected_arrival: '', expected_items: [], merchant_id: '' });
      setExpectedLines([{ ...EMPTY_LINE }]);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to create receipt');
    }
  };

  const addExpectedLine = () => setExpectedLines((l) => [...l, { ...EMPTY_LINE }]);
  const removeExpectedLine = (i) => setExpectedLines((l) => l.length > 1 ? l.filter((_, idx) => idx !== i) : l);
  const setExpectedLine = (i, key, value) => setExpectedLines((l) => l.map((ln, idx) => idx === i ? { ...ln, [key]: value } : ln));

  // Find the most recent receipt for the currently-typed supplier name that
  // actually has expected_items so we can offer a one-tap copy. Match is
  // case-insensitive + trimmed so trailing-space typos don't lose the match.
  const lastReceiptForSupplier = useMemo(() => {
    const name = (newReceipt.supplier_name || '').trim().toLowerCase();
    if (!name) return null;
    return receipts.find((r) => {
      if (!r?.expected_items?.length) return false;
      return (r.supplier_name || '').trim().toLowerCase() === name;
    }) || null;
  }, [receipts, newReceipt.supplier_name]);

  // Saved supplier template — wins over "last receipt" because it's the
  // canonical line-set ops explicitly curated. Same case-insensitive rule.
  const templateForSupplier = useMemo(() => {
    const name = (newReceipt.supplier_name || '').trim().toLowerCase();
    if (!name) return null;
    return templates.find((t) => (t.supplier_lc || (t.supplier_name || '').toLowerCase()) === name) || null;
  }, [templates, newReceipt.supplier_name]);

  const copyFromLastReceipt = () => {
    if (!lastReceiptForSupplier?.expected_items?.length) return;
    const next = lastReceiptForSupplier.expected_items.map((it) => ({
      variant_id: it.variant_id || '',
      expected_qty: String(it.expected_qty || ''),
    }));
    setExpectedLines(next.length ? next : [{ ...EMPTY_LINE }]);
    toast.success(`Copied ${next.length} line${next.length === 1 ? '' : 's'} from ${lastReceiptForSupplier.receipt_number}`);
  };

  const useTemplate = () => {
    if (!templateForSupplier?.items?.length) return;
    const next = templateForSupplier.items.map((it) => ({
      variant_id: it.variant_id || '',
      expected_qty: String(it.expected_qty || ''),
    }));
    setExpectedLines(next.length ? next : [{ ...EMPTY_LINE }]);
    toast.success(`Loaded "${templateForSupplier.name}" template (${next.length} line${next.length === 1 ? '' : 's'})`);
  };

  const handleSaveTemplate = async () => {
    const supplier = (newReceipt.supplier_name || '').trim();
    if (!supplier) { toast.error('Enter a supplier name first'); return; }
    // Reuse the same shape we send on receipt create so the template "round-
    // trips" perfectly into a future receipt.
    const acc = new Map();
    for (const ln of expectedLines) {
      if (!ln.variant_id) continue;
      const qty = parseInt(ln.expected_qty, 10);
      if (!Number.isFinite(qty) || qty <= 0) continue;
      acc.set(ln.variant_id, (acc.get(ln.variant_id) || 0) + qty);
    }
    if (acc.size === 0) { toast.error('Add at least one expected line'); return; }
    const items = Array.from(acc.entries()).map(([variant_id, expected_qty]) => {
      const v = variants.find((x) => x.id === variant_id);
      const sku = v && skus.find((s) => s.id === v.sku_id);
      return {
        variant_id,
        expected_qty,
        sku_id: v?.sku_id || null,
        sku_code: sku?.sku_code || null,
        description: sku?.name || null,
        barcode: v?.barcode || null,
        size: v?.size || null,
        color: v?.color || null,
      };
    });
    setSavingTemplate(true);
    try {
      const res = await saveReceiptTemplate({ supplier_name: supplier, name: supplier, items });
      toast.success(res.data?.created ? `Saved "${supplier}" template` : `Updated "${supplier}" template`);
      const fresh = await getReceiptTemplates();
      setTemplates(fresh.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateForSupplier?.id) return;
    if (!window.confirm(`Delete "${templateForSupplier.name}" template? Future receipts won't auto-fill from it.`)) return;
    try {
      await deleteReceiptTemplate(templateForSupplier.id);
      const fresh = await getReceiptTemplates();
      setTemplates(fresh.data || []);
      toast.success('Template removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const uniqueSuppliers = useMemo(() => {
    const set = new Set(receipts.map(r => r.supplier_name).filter(Boolean));
    return Array.from(set).sort();
  }, [receipts]);

  const filteredReceipts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return receipts.filter(r => {
      if (term) {
        const hay = `${r.receipt_number || ''} ${r.supplier_name || ''} ${r.po_number || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (supplierFilter && r.supplier_name !== supplierFilter) return false;
      if (statusFilter) {
        // Real DB statuses
        if (['pending', 'in_progress', 'completed'].includes(statusFilter)) {
          if (r.status !== statusFilter) return false;
        } else {
          // Derived buckets — operate on completed (full vs short) and
          // open (any non-completed receipt with at least one expected
          // line still outstanding).
          const expectedTotal = (r.expected_items || []).reduce(
            (s, it) => s + (parseInt(it.expected_qty, 10) || 0), 0);
          const receivedTotal = (r.received_items || []).reduce(
            (s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
          const outstanding = Math.max(0, expectedTotal - receivedTotal);
          if (statusFilter === 'open_partial') {
            // Live receipts with progress + outstanding qty (the "still
            // waiting" bucket — the original ask).
            if (r.status === 'completed') return false;
            if (expectedTotal === 0) return false;
            if (receivedTotal === 0) return false;
            if (outstanding === 0) return false;
          } else if (statusFilter === 'fully_received') {
            // Completed + everything we expected actually arrived.
            if (r.status !== 'completed') return false;
            if (expectedTotal === 0) return false;
            if (outstanding > 0) return false;
          } else if (statusFilter === 'completed_short') {
            // Completed with a shortage (closed-with-short-ship path).
            if (r.status !== 'completed') return false;
            if (!(outstanding > 0 || r.shortage_reason)) return false;
          }
        }
      }
      if (etaWindow) {
        const isCompleted = r.status === 'completed';
        if (!matchesEtaWindow(r.expected_arrival, etaWindow, { isCompleted })) return false;
      }
      if (!matchesCreatedWindow(r.created_at, dateWindow)) return false;
      return true;
    });
  }, [receipts, searchTerm, supplierFilter, statusFilter, etaWindow, dateWindow]);

  const startReceiving = (receipt) => {
    setActiveReceipt(receipt);
  };

  if (activeReceipt) {
    return (
      <Layout>
        <ReceivingDetail
          receipt={activeReceipt}
          locations={locations}
          variants={variants}
          skus={skus}
          onExit={() => setActiveReceipt(null)}
          onReceiptRefresh={(fresh) => {
            setReceipts(fresh);
            const updated = fresh.find((r) => r.id === activeReceipt.id);
            if (updated) setActiveReceipt(updated);
          }}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="receiving-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              Receiving
              <HelpAnchor articleId="receiving" label="Receiving flow & serial capture" />
            </h1>
            <p className="text-muted-foreground">Receive stock and manage purchase orders</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="create-receipt-btn" className="bg-brand hover:bg-brand/90">
                <Plus size={18} className="mr-2" /> New Receipt
              </Button>
            </DialogTrigger>
            <DialogContent className="border border-border rounded-none max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="create-receipt-dialog">
              <DialogHeader>
                <DialogTitle>Create Receipt</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Add the SKUs and quantities you're expecting so receivers can tick them off as they unload.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="overline">Supplier Name</Label>
                    <Input value={newReceipt.supplier_name} onChange={(e) => setNewReceipt({...newReceipt, supplier_name: e.target.value})} required className="mt-1" data-testid="receipt-supplier-input" />
                  </div>
                  <div>
                    <Label className="overline">PO Number (Optional)</Label>
                    <Input value={newReceipt.po_number} onChange={(e) => setNewReceipt({...newReceipt, po_number: e.target.value})} className="mt-1" data-testid="receipt-po-input" />
                  </div>
                  <div>
                    <Label className="overline">Expected Arrival (Optional)</Label>
                    <Input
                      type="date"
                      data-testid="receipt-expected-arrival"
                      value={newReceipt.expected_arrival || ''}
                      onChange={(e) => setNewReceipt({...newReceipt, expected_arrival: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="overline">Notes</Label>
                    <Input value={newReceipt.notes} onChange={(e) => setNewReceipt({...newReceipt, notes: e.target.value})} className="mt-1" data-testid="receipt-notes-input" />
                  </div>
                  {hasThreePlBilling && merchants.length > 0 && (
                    <div className="md:col-span-2">
                      <Label className="overline">3PL Merchant (Optional)</Label>
                      <select
                        value={newReceipt.merchant_id || ''}
                        onChange={(e) => setNewReceipt({ ...newReceipt, merchant_id: e.target.value })}
                        className="mt-1 w-full border border-border bg-background px-2 py-2 text-sm"
                        data-testid="receipt-merchant-select"
                      >
                        <option value="">— Use default merchant —</option>
                        {merchants.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}{m.is_default ? ' (default)' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Inventory received against this PO will be billed to the selected merchant
                        (per-merchant storage fees, inbound charges, etc).
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <div>
                      <Label className="overline">Expected Items</Label>
                      <p className="text-[11px] text-muted-foreground">Receivers will see this as a tickable checklist.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {templateForSupplier && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={useTemplate}
                            data-testid="use-template-btn"
                            className="rounded-none border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            title={`Load ${templateForSupplier.items?.length || 0} line(s) from "${templateForSupplier.name}" template`}
                          >
                            <FileArrowDown size={14} className="mr-1" />
                            Use "{templateForSupplier.name}" template
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleDeleteTemplate}
                            data-testid="delete-template-btn"
                            className="rounded-none text-rose-600 hover:bg-rose-50"
                            title="Delete saved template"
                          >
                            <Trash size={14} />
                          </Button>
                        </>
                      )}
                      {!templateForSupplier && lastReceiptForSupplier && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={copyFromLastReceipt}
                          data-testid="copy-from-last-btn"
                          className="rounded-none"
                          title={`Copy ${lastReceiptForSupplier.expected_items.length} line(s) from ${lastReceiptForSupplier.receipt_number}`}
                        >
                          <FileArrowDown size={14} className="mr-1" />
                          Copy from {lastReceiptForSupplier.receipt_number}
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSaveTemplate}
                        disabled={savingTemplate}
                        data-testid="save-template-btn"
                        className="rounded-none"
                        title="Save current lines as the supplier's reusable template"
                      >
                        <FloppyDisk size={14} className="mr-1" />
                        {savingTemplate ? 'Saving…' : (templateForSupplier ? 'Update template' : 'Save as template')}
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={addExpectedLine} data-testid="add-expected-line-btn" className="rounded-none">
                        <Plus size={14} className="mr-1" /> Add Line
                      </Button>
                    </div>
                  </div>
                  <div className="border border-border rounded-md">
                    <table className="w-full table-dense">
                      <thead className="sticky top-0 bg-card">
                        <tr>
                          <th>Variant / SKU</th>
                          <th className="text-right w-32">Expected Qty</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {expectedLines.map((ln, i) => (
                          <tr key={i} data-testid={`expected-line-${i}`}>
                            <td>
                              <SkuVariantPicker
                                skus={skus}
                                variants={variants}
                                value={ln.variant_id}
                                onChange={(vid) => setExpectedLine(i, 'variant_id', vid)}
                                testIdPrefix={`expected-picker-${i}`}
                                placeholder="Type SKU code, name, or barcode…"
                              />
                            </td>
                            <td className="text-right">
                              <Input
                                type="number"
                                inputMode="numeric"
                                min={1}
                                value={ln.expected_qty}
                                onChange={(e) => setExpectedLine(i, 'expected_qty', e.target.value)}
                                data-testid={`expected-qty-${i}`}
                                className="text-right font-mono"
                              />
                            </td>
                            <td className="text-right">
                              {expectedLines.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeExpectedLine(i)}
                                  data-testid={`remove-expected-line-${i}`}
                                  className="text-muted-foreground hover:text-red-600"
                                  aria-label="Remove line"
                                >
                                  <X size={14} weight="bold" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-1.5 text-[11px] text-muted-foreground">
                    Leave blank to create a header-only receipt — receivers will scan/log freely without a checklist.
                  </p>
                </div>

                <Button type="submit" data-testid="submit-create-receipt-btn" className="w-full bg-brand hover:bg-brand/90">Create Receipt</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <FilterBar
          testId="receiving-filter"
          search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Search receipt #, supplier, PO…' }}
          filters={[
            {
              key: 'supplier', value: supplierFilter, onChange: setSupplierFilter, label: 'Supplier',
              options: [{ value: '', label: 'All suppliers' }, ...uniqueSuppliers.map(s => ({ value: s, label: s }))],
            },
            {
              key: 'status', value: statusFilter, onChange: setStatusFilter, label: 'Status',
              options: [
                { value: '', label: 'All statuses' },
                { value: 'pending', label: 'Pending · not started' },
                { value: 'in_progress', label: 'In progress · live' },
                { value: 'open_partial', label: 'Partial · outstanding qty' },
                { value: 'completed', label: 'Completed · any' },
                { value: 'fully_received', label: 'Completed · fully received' },
                { value: 'completed_short', label: 'Completed · with shortage' },
              ],
            },
            {
              key: 'eta', value: etaWindow, onChange: setEtaWindow, label: 'ETA window',
              options: ETA_WINDOW_OPTIONS,
            },
            {
              key: 'date', value: dateWindow, onChange: setDateWindow, label: 'Created window',
              options: CREATED_WINDOW_OPTIONS,
            },
          ]}
          onClear={clearUrlFilters}
        />

        <div className="grid gap-4">
          {loading ? (
            [1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 animate-pulse"></div>)
          ) : filteredReceipts.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <FileArrowDown size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-muted-foreground">
                {receipts.length === 0 ? 'No receipts found' : 'No receipts match current filters'}
              </p>
            </div>
          ) : filteredReceipts.map(receipt => {
            const receivedCount = receipt.received_items?.length || 0;
            const expectedItems = receipt.expected_items || [];
            const expectedTotal = expectedItems.reduce((s, it) => s + (parseInt(it.expected_qty, 10) || 0), 0);
            const receivedTotal = (receipt.received_items || []).reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
            const outstanding = Math.max(0, expectedTotal - receivedTotal);
            const isCompletedShort = receipt.status === 'completed' && (outstanding > 0 || receipt.shortage_reason);
            const isActivePartial = receipt.status !== 'completed' && expectedTotal > 0 && receivedTotal > 0 && outstanding > 0;
            const etaChip = getEtaChip(receipt.expected_arrival, { isCompleted: receipt.status === 'completed' });

            return (
              <div key={receipt.id} className="bg-card border border-border rounded-lg p-5" data-testid={`receipt-row-${receipt.receipt_number}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <p className="font-mono font-bold text-lg">{receipt.receipt_number}</p>
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${
                        receipt.status === 'completed' ? 'bg-green-100 text-green-800' :
                        receipt.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {receipt.status}
                      </span>
                      {etaChip && (
                        <span
                          data-testid={`receipt-eta-${receipt.receipt_number}`}
                          className={`px-2 py-1 text-[11px] font-bold uppercase tracking-wider ${etaChip.cls}`}
                        >
                          {etaChip.label}
                        </span>
                      )}
                      {isActivePartial && (
                        <span
                          data-testid={`receipt-outstanding-${receipt.receipt_number}`}
                          className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider bg-blue-100 text-blue-800"
                          title={`${outstanding} units still expected — keep receiving or close with shortage.`}
                        >
                          {outstanding} outstanding
                        </span>
                      )}
                      {isCompletedShort && (
                        <span
                          data-testid={`receipt-short-${receipt.receipt_number}`}
                          className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800"
                          title={receipt.shortage_notes || receipt.shortage_reason || `${outstanding} units short on close`}
                        >
                          Short · {outstanding || '?'}{receipt.shortage_reason ? ` · ${receipt.shortage_reason.replace(/_/g, ' ')}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      <span>{receipt.supplier_name}</span>
                      {receipt.po_number && <span>PO: {receipt.po_number}</span>}
                      {expectedTotal > 0 ? (
                        <span data-testid={`receipt-progress-${receipt.receipt_number}`} className="font-mono">
                          {receivedTotal}/{expectedTotal} units
                          <span className="ml-1">· {expectedItems.length} line{expectedItems.length === 1 ? '' : 's'}</span>
                        </span>
                      ) : (
                        <span>{receivedCount} items received</span>
                      )}
                    </div>
                  </div>
                  {receipt.status !== 'completed' && (
                    <Button onClick={() => startReceiving(receipt)} data-testid="start-receiving-btn" className="bg-brand hover:bg-brand/90">
                      <Play size={18} className="mr-2" /> Start Receiving
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPalletLogReceipt(receipt)}
                    data-testid={`receipt-log-pallets-${receipt.receipt_number}`}
                    title="Log pallets brought in by this receipt — flows into the Pallets module + 3PL daily hire."
                  >
                    <Stack size={16} className="mr-2" /> Log pallets
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <PalletQuickLogDialog
        open={!!palletLogReceipt}
        setOpen={(v) => { if (!v) setPalletLogReceipt(null); }}
        source="receipt"
        reference={palletLogReceipt?.receipt_number}
        merchantId={palletLogReceipt?.merchant_id}
        onLogged={() => setPalletLogReceipt(null)}
      />
    </Layout>
  );
}
